import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  X, FileSpreadsheet, FileText, Loader2, Type, Rows3, LayoutList, Plus, Search, Columns3,
} from "lucide-react";

// V1.71 · Hoja imprimible tipo Excel: se ve antes de descargar, se filtra por
// etiquetas manuales (lista de Configuración) y se exporta a Excel o PDF.
const SIZES = [
  { key: "s", label: "Pequeña", cell: "text-[10px]", head: "text-[9px]", pad: "px-1.5 py-0.5" },
  { key: "m", label: "Normal", cell: "text-xs", head: "text-[10px]", pad: "px-2 py-1" },
  { key: "l", label: "Grande", cell: "text-sm", head: "text-xs", pad: "px-2.5 py-1.5" },
];

const DEFAULT_COLS = ["nombre", "primer_apellido", "segundo_apellido", "dni", "fecha_nacimiento", "observaciones"];
const NO_TAG = "SIN ETIQUETA";

export default function PrintableSheetDialog({ open, onClose, students }) {
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [columns, setColumns] = useState([]);
  const [cols, setCols] = useState(DEFAULT_COLS);
  const [size, setSize] = useState(1);
  const [grouped, setGrouped] = useState(true);
  const [search, setSearch] = useState("");
  const [showCols, setShowCols] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [busy, setBusy] = useState("");

  const loadTags = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/lookup/listado_filters");
      setTags(data?.values || []);
    } catch {
      setTags([]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadTags();
    api.get("/students/export/columns").then(({ data }) => setColumns(data || [])).catch(() => setColumns([]));
  }, [open, loadTags]);

  const addTag = async () => {
    const value = newTag.trim();
    if (!value) return;
    try {
      const { data } = await api.post("/settings/lookup/listado_filters/append", { value });
      setTags(data?.values || []);
      setNewTag("");
      toast.success(`Filtro «${value}» añadido`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo añadir");
    }
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (students || []).filter((s) => {
      const st = s.list_tags || [];
      if (selectedTags.length && !selectedTags.some((t) => st.includes(t))) return false;
      if (!q) return true;
      return [s.name, s.primer_apellido, s.segundo_apellido, s.dni, s.notes]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [students, selectedTags, search]);

  const sections = useMemo(() => {
    if (!grouped) return [{ label: "", items: rows }];
    const groups = new Map();
    const order = selectedTags.length ? selectedTags : tags;
    for (const t of order) groups.set(t, []);
    const orphans = [];
    for (const s of rows) {
      const own = (s.list_tags || []).filter((t) => groups.has(t));
      if (own.length === 0) orphans.push(s);
      else own.forEach((t) => groups.get(t).push(s));
    }
    const out = [...groups.entries()].filter(([, v]) => v.length > 0).map(([label, items]) => ({ label, items }));
    if (orphans.length) out.push({ label: NO_TAG, items: orphans });
    return out;
  }, [rows, grouped, tags, selectedTags]);

  // Un alumno con varias etiquetas activas aparece en cada sección: el contador
  // distingue registros únicos de filas impresas (V1.72).
  const uniqueCount = rows.length;
  const totalRows = useMemo(() => sections.reduce((a, s) => a + s.items.length, 0), [sections]);

  const colDefs = useMemo(    () => cols.map((k) => ({ key: k, label: (columns.find((c) => c.key === k) || {}).label || k })),
    [cols, columns],
  );

  const cellValue = (s, key) => {
    switch (key) {
      case "nombre": return s.name || "";
      case "primer_apellido": return s.primer_apellido || "";
      case "segundo_apellido": return s.segundo_apellido || "";
      case "dni": return s.dni || "";
      case "telefono": return s.phone || "";
      case "email": return s.email || "";
      case "fecha_nacimiento": return s.birth_date || "";
      case "codigo_postal": return s.codigo_postal || "";
      case "localidad": return s.localidad || "";
      case "codigo_expediente": return s.codigo_expediente || "";
      case "estado": return s.status || (s.active === false ? "inactivo" : "activo");
      case "observaciones": return s.notes || "";
      case "etiquetas": return (s.list_tags || []).join(", ");
      case "facturas_emitidas":
        return String((s.invoices || []).filter((i) => i && i.emitted && String(i.number || "").trim()).length);
      case "alta": return (s.created_at || "").slice(0, 10);
      case "direccion":
        return [s.tipo_via, s.nombre_via, s.numero_via, s.codigo_postal, s.localidad, s.provincia]
          .filter(Boolean).join(" ");
      default: return "";
    }
  };

  const doExport = async (format) => {
    if (cols.length === 0) { toast.error("Selecciona al menos una columna"); return; }
    if (rows.length === 0) { toast.error("No hay filas que exportar"); return; }
    setBusy(format);
    try {
      const body = grouped
        ? { columns: cols, format, sections: sections.map((s) => ({ label: s.label, student_ids: s.items.map((x) => x.id) })) }
        : { columns: cols, format, student_ids: rows.map((x) => x.id) };
      const res = await api.post("/students/export", body, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const fname = (cd.match(/filename="([^"]+)"/) || [])[1] || `listado.${format}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Descarga lista");
    } catch {
      toast.error("No se pudo exportar");
    } finally {
      setBusy("");
    }
  };

  const S = SIZES[size];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-[98vw] w-[98vw] h-[94vh] p-0 gap-0 overflow-hidden flex flex-col"
        data-testid="printable-sheet-dialog"
      >
        <DialogTitle className="sr-only">Hoja imprimible de alumnos</DialogTitle>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-neutral-200 bg-neutral-50 shrink-0 flex-wrap">
          <FileSpreadsheet className="h-4 w-4 text-orange-600" />
          <span className="font-bold text-sm text-neutral-800">Hoja imprimible de alumnos</span>
          <span className="text-xs text-neutral-500" data-testid="sheet-count">
            {uniqueCount} registro{uniqueCount !== 1 ? "s" : ""} · {totalRows} fila{totalRows !== 1 ? "s" : ""} · {sections.length} secci{sections.length !== 1 ? "ones" : "ón"}
          </span>
          <div className="ml-auto flex items-center gap-1.5 flex-wrap">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-neutral-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar…"
                data-testid="sheet-search"
                className="h-8 w-40 rounded-lg border border-neutral-300 pl-7 pr-2 text-xs" />
            </div>
            <button type="button" onClick={() => setGrouped((g) => !g)} data-testid="sheet-toggle-grouped"
              className="h-8 rounded-lg border border-neutral-300 bg-white px-2.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 inline-flex items-center gap-1.5">
              {grouped ? <Rows3 className="h-3.5 w-3.5" /> : <LayoutList className="h-3.5 w-3.5" />}
              {grouped ? "Agrupado" : "Tabla continua"}
            </button>
            <button type="button" onClick={() => setSize((i) => (i + 1) % SIZES.length)} data-testid="sheet-font-size"
              title="Cambiar tamaño de letra"
              className="h-8 rounded-lg border border-neutral-300 bg-white px-2.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 inline-flex items-center gap-1.5">
              <Type className="h-3.5 w-3.5" /> {S.label}
            </button>
            <button type="button" onClick={() => setShowCols((v) => !v)} data-testid="sheet-toggle-columns"
              className="h-8 rounded-lg border border-neutral-300 bg-white px-2.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 inline-flex items-center gap-1.5">
              <Columns3 className="h-3.5 w-3.5" /> Columnas ({cols.length})
            </button>
            <button type="button" disabled={!!busy} onClick={() => doExport("xlsx")} data-testid="sheet-export-xlsx"
              className="h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy === "xlsx" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />} Excel
            </button>
            <button type="button" disabled={!!busy} onClick={() => doExport("pdf")} data-testid="sheet-export-pdf"
              className="h-8 rounded-lg bg-neutral-800 hover:bg-neutral-900 text-white px-2.5 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} PDF
            </button>
            <button type="button" onClick={onClose} data-testid="sheet-close"
              className="h-8 w-8 rounded-lg text-neutral-500 hover:bg-neutral-200 inline-flex items-center justify-center">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="px-4 py-2 border-b border-neutral-200 flex items-center gap-1.5 flex-wrap shrink-0" data-testid="sheet-tag-filters">
          {tags.map((t) => {
            const on = selectedTags.includes(t);
            return (
              <button key={t} type="button" data-testid={`sheet-tag-${t}`}
                onClick={() => setSelectedTags((p) => (on ? p.filter((x) => x !== t) : [...p, t]))}
                className={"h-7 rounded-full px-2.5 text-[11px] font-bold uppercase tracking-wide border transition-colors " +
                  (on ? "bg-orange-500 border-orange-500 text-white" : "bg-white border-neutral-300 text-neutral-600 hover:border-orange-300")}>
                {t}
              </button>
            );
          })}
          {selectedTags.length > 0 && (
            <button type="button" onClick={() => setSelectedTags([])} data-testid="sheet-tags-clear"
              className="h-7 rounded-full px-2.5 text-[11px] font-semibold text-neutral-500 hover:text-neutral-900">
              Limpiar
            </button>
          )}
          <div className="ml-auto flex items-center gap-1">
            <input value={newTag} onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addTag()}
              placeholder="Nuevo filtro…" data-testid="sheet-new-tag-input"
              className="h-7 w-36 rounded-lg border border-neutral-300 px-2 text-[11px]" />
            <button type="button" onClick={addTag} data-testid="sheet-new-tag-add"
              className="h-7 rounded-lg border border-orange-300 bg-orange-50 text-orange-700 px-2 text-[11px] font-bold inline-flex items-center gap-1">
              <Plus className="h-3 w-3" /> Añadir
            </button>
          </div>
        </div>

        {showCols && (
          <div className="px-4 py-2 border-b border-neutral-200 bg-neutral-50 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-1.5 shrink-0" data-testid="sheet-columns-picker">
            {columns.map((c) => (
              <label key={c.key} className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                <input type="checkbox" checked={cols.includes(c.key)} data-testid={`sheet-col-${c.key}`}
                  onChange={() => setCols((p) => (p.includes(c.key) ? p.filter((x) => x !== c.key) : [...p, c.key]))}
                  className="h-3.5 w-3.5 accent-orange-500" />
                {c.label}
              </label>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-auto bg-white p-4" data-testid="sheet-body">
          {sections.length === 0 && (
            <div className="text-sm text-neutral-500 py-8 text-center">Sin registros con estos filtros.</div>
          )}
          {sections.map((sec, si) => (
            <table key={sec.label || si} className="w-full border-collapse mb-6" data-testid={`sheet-section-${sec.label || "todos"}`}>
              <thead>
                {sec.label && (
                  <tr>
                    <th colSpan={colDefs.length} className={"bg-black text-white font-bold uppercase text-center border border-black " + S.head + " " + S.pad}>
                      {sec.label} ({sec.items.length})
                    </th>
                  </tr>
                )}
                <tr>
                  {colDefs.map((c) => (
                    <th key={c.key} className={"bg-orange-500 text-white font-bold text-left border border-orange-600 whitespace-nowrap " + S.head + " " + S.pad}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sec.items.map((s) => (
                  <tr key={s.id} className="hover:bg-orange-50/60" data-testid={`sheet-row-${s.id}`}>
                    {colDefs.map((c) => (
                      <td key={c.key} className={"border border-neutral-300 text-neutral-800 align-top " + S.cell + " " + S.pad}>
                        {cellValue(s, c.key)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
